<script setup>
import { ref, computed } from 'vue';
import { onClickOutside } from '@vueuse/core';

const props = defineProps({
  editor: { type: Object, required: true },
});

const FONT_FAMILIES = [
  'Arial',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Courier New',
  'Roboto',
];

const FONT_SIZES = [
  '8pt',
  '9pt',
  '10pt',
  '11pt',
  '12pt',
  '14pt',
  '18pt',
  '24pt',
  '30pt',
  '36pt',
  '48pt',
  '72pt',
];

const LINE_HEIGHTS = [
  { value: '1', label: 'Sencillo' },
  { value: '1.15', label: '1.15' },
  { value: '1.5', label: '1.5' },
  { value: '2', label: 'Doble' },
];

const PALETTE = [
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#b7b7b7',
  '#cccccc',
  '#d9d9d9',
  '#efefef',
  '#f3f3f3',
  '#ffffff',
  '#980000',
  '#ff0000',
  '#ff9900',
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#4a86e8',
  '#0000ff',
  '#9900ff',
  '#ff00ff',
];

const editor = computed(() => props.editor);

const currentHeading = computed(() => {
  const ed = editor.value;
  if (!ed) return 'paragraph';
  for (let level = 1; level <= 4; level += 1) {
    if (ed.isActive('heading', { level })) return `h${level}`;
  }
  return 'paragraph';
});

const currentFontFamily = computed(
  () => editor.value?.getAttributes('textStyle').fontFamily || 'Arial'
);
const currentFontSize = computed(
  () => editor.value?.getAttributes('textStyle').fontSize || '11pt'
);
const currentLineHeight = computed(
  () => editor.value?.getAttributes('paragraph').lineHeight || '1.5'
);

function setHeading(value) {
  const chain = editor.value.chain().focus();
  if (value === 'paragraph') {
    chain.setParagraph().run();
  } else {
    chain.toggleHeading({ level: Number(value.replace('h', '')) }).run();
  }
}

const colorOpen = ref(false);
const highlightOpen = ref(false);
const lineHeightOpen = ref(false);
const linkOpen = ref(false);
const linkUrl = ref('');

const colorRef = ref(null);
const highlightRef = ref(null);
const lineHeightRef = ref(null);
const linkRef = ref(null);

onClickOutside(colorRef, () => {
  colorOpen.value = false;
});
onClickOutside(highlightRef, () => {
  highlightOpen.value = false;
});
onClickOutside(lineHeightRef, () => {
  lineHeightOpen.value = false;
});
onClickOutside(linkRef, () => {
  linkOpen.value = false;
});

function applyColor(color) {
  editor.value.chain().focus().setColor(color).run();
  colorOpen.value = false;
}
function applyHighlight(color) {
  editor.value.chain().focus().toggleHighlight({ color }).run();
  highlightOpen.value = false;
}
function applyLineHeight(value) {
  editor.value.chain().focus().setLineHeight(value).run();
  lineHeightOpen.value = false;
}
function openLink() {
  linkUrl.value = editor.value.getAttributes('link').href || '';
  linkOpen.value = true;
}
function applyLink() {
  const chain = editor.value.chain().focus().extendMarkRange('link');
  if (linkUrl.value) {
    chain.setLink({ href: linkUrl.value }).run();
  } else {
    chain.unsetLink().run();
  }
  linkOpen.value = false;
}
function insertImage() {
  // eslint-disable-next-line no-alert
  const url = window.prompt('URL de la imagen');
  if (url) editor.value.chain().focus().setImage({ src: url }).run();
}
function insertTable() {
  editor.value
    .chain()
    .focus()
    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
    .run();
}
</script>

<template>
  <div
    class="flex flex-wrap gap-0.5 items-center p-1.5 border-b border-n-weak bg-n-alpha-1"
  >
    <!-- Undo / Redo -->
    <button
      class="tb-btn"
      :disabled="!editor.can().undo()"
      title="Deshacer"
      @click="editor.chain().focus().undo().run()"
    >
      <span class="i-lucide-undo-2 size-4" />
    </button>
    <button
      class="tb-btn"
      :disabled="!editor.can().redo()"
      title="Rehacer"
      @click="editor.chain().focus().redo().run()"
    >
      <span class="i-lucide-redo-2 size-4" />
    </button>

    <span class="tb-divider" />

    <!-- Block style -->
    <select
      class="tb-select w-28"
      :value="currentHeading"
      @change="setHeading($event.target.value)"
    >
      <option value="paragraph">Párrafo</option>
      <option value="h1">Título 1</option>
      <option value="h2">Título 2</option>
      <option value="h3">Título 3</option>
      <option value="h4">Título 4</option>
    </select>

    <!-- Font family -->
    <select
      class="tb-select w-32"
      :value="currentFontFamily"
      @change="editor.chain().focus().setFontFamily($event.target.value).run()"
    >
      <option v-for="font in FONT_FAMILIES" :key="font" :value="font">
        {{ font }}
      </option>
    </select>

    <!-- Font size -->
    <select
      class="tb-select w-16"
      :value="currentFontSize"
      @change="editor.chain().focus().setFontSize($event.target.value).run()"
    >
      <option v-for="size in FONT_SIZES" :key="size" :value="size">
        {{ size.replace('pt', '') }}
      </option>
    </select>

    <span class="tb-divider" />

    <!-- Inline marks -->
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive('bold') }"
      title="Negrita"
      @click="editor.chain().focus().toggleBold().run()"
    >
      <span class="i-lucide-bold size-4" />
    </button>
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive('italic') }"
      title="Cursiva"
      @click="editor.chain().focus().toggleItalic().run()"
    >
      <span class="i-lucide-italic size-4" />
    </button>
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive('underline') }"
      title="Subrayado"
      @click="editor.chain().focus().toggleUnderline().run()"
    >
      <span class="i-lucide-underline size-4" />
    </button>
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive('strike') }"
      title="Tachado"
      @click="editor.chain().focus().toggleStrike().run()"
    >
      <span class="i-lucide-strikethrough size-4" />
    </button>

    <span class="tb-divider" />

    <!-- Text color -->
    <div ref="colorRef" class="relative">
      <button
        class="tb-btn"
        title="Color de texto"
        @click="colorOpen = !colorOpen"
      >
        <span class="i-lucide-baseline size-4" />
      </button>
      <div v-if="colorOpen" class="tb-palette">
        <button
          v-for="color in PALETTE"
          :key="`c-${color}`"
          class="tb-swatch"
          :style="{ backgroundColor: color }"
          @click="applyColor(color)"
        />
      </div>
    </div>

    <!-- Highlight -->
    <div ref="highlightRef" class="relative">
      <button
        class="tb-btn"
        title="Resaltar"
        @click="highlightOpen = !highlightOpen"
      >
        <span class="i-lucide-highlighter size-4" />
      </button>
      <div v-if="highlightOpen" class="tb-palette">
        <button
          v-for="color in PALETTE"
          :key="`h-${color}`"
          class="tb-swatch"
          :style="{ backgroundColor: color }"
          @click="applyHighlight(color)"
        />
      </div>
    </div>

    <span class="tb-divider" />

    <!-- Alignment -->
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive({ textAlign: 'left' }) }"
      title="Alinear a la izquierda"
      @click="editor.chain().focus().setTextAlign('left').run()"
    >
      <span class="i-lucide-align-left size-4" />
    </button>
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive({ textAlign: 'center' }) }"
      title="Centrar"
      @click="editor.chain().focus().setTextAlign('center').run()"
    >
      <span class="i-lucide-align-center size-4" />
    </button>
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive({ textAlign: 'right' }) }"
      title="Alinear a la derecha"
      @click="editor.chain().focus().setTextAlign('right').run()"
    >
      <span class="i-lucide-align-right size-4" />
    </button>
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive({ textAlign: 'justify' }) }"
      title="Justificar"
      @click="editor.chain().focus().setTextAlign('justify').run()"
    >
      <span class="i-lucide-align-justify size-4" />
    </button>

    <!-- Line height -->
    <div ref="lineHeightRef" class="relative">
      <button
        class="gap-1 px-2 tb-btn"
        title="Interlineado"
        @click="lineHeightOpen = !lineHeightOpen"
      >
        <span class="i-lucide-chevrons-up-down size-4" />
        <span class="text-xs">{{ currentLineHeight }}</span>
      </button>
      <div v-if="lineHeightOpen" class="flex flex-col p-1 tb-menu w-36">
        <button
          v-for="lh in LINE_HEIGHTS"
          :key="lh.value"
          class="tb-menu-item"
          :class="{ 'tb-active': currentLineHeight === lh.value }"
          @click="applyLineHeight(lh.value)"
        >
          {{ lh.label }}
        </button>
      </div>
    </div>

    <span class="tb-divider" />

    <!-- Lists -->
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive('bulletList') }"
      title="Lista con viñetas"
      @click="editor.chain().focus().toggleBulletList().run()"
    >
      <span class="i-lucide-list size-4" />
    </button>
    <button
      class="tb-btn"
      :class="{ 'tb-active': editor.isActive('orderedList') }"
      title="Lista numerada"
      @click="editor.chain().focus().toggleOrderedList().run()"
    >
      <span class="i-lucide-list-ordered size-4" />
    </button>

    <span class="tb-divider" />

    <!-- Link -->
    <div ref="linkRef" class="relative">
      <button
        class="tb-btn"
        :class="{ 'tb-active': editor.isActive('link') }"
        title="Insertar enlace"
        @click="openLink"
      >
        <span class="i-lucide-link size-4" />
      </button>
      <div v-if="linkOpen" class="p-2 tb-menu w-72">
        <input
          v-model="linkUrl"
          type="url"
          placeholder="https://…"
          class="px-2 py-1 w-full text-sm rounded-md border border-n-weak bg-n-alpha-black2"
          @keyup.enter="applyLink"
        />
        <div class="flex gap-2 mt-2">
          <button class="tb-primary" @click="applyLink">Aplicar</button>
        </div>
      </div>
    </div>

    <!-- Image -->
    <button class="tb-btn" title="Insertar imagen" @click="insertImage">
      <span class="i-lucide-image size-4" />
    </button>

    <!-- Table -->
    <button class="tb-btn" title="Insertar tabla" @click="insertTable">
      <span class="i-lucide-table size-4" />
    </button>

    <span class="tb-divider" />

    <!-- Columns (e.g. a two-column signature row) -->
    <button
      class="tb-btn"
      title="Insertar columnas"
      @click="editor.chain().focus().insertColumns(2).run()"
    >
      <span class="i-lucide-columns-2 size-4" />
    </button>

    <!-- Page break -->
    <button
      class="tb-btn"
      title="Insertar salto de página"
      @click="editor.chain().focus().insertPageBreak().run()"
    >
      <span class="i-lucide-separator-horizontal size-4" />
    </button>
  </div>
</template>

<style scoped>
.tb-btn {
  @apply inline-flex items-center justify-center h-8 min-w-8 px-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-40 disabled:pointer-events-none;
}
.tb-active {
  @apply bg-n-alpha-2 text-n-slate-12;
}
.tb-divider {
  @apply w-px h-5 mx-1 bg-n-weak;
}
.tb-select {
  @apply h-8 px-1.5 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12;
}
.tb-palette {
  @apply absolute z-50 top-full left-0 mt-1 grid grid-cols-10 gap-1 p-2 rounded-lg border shadow-lg border-n-weak bg-n-solid-1;
}
.tb-swatch {
  @apply w-5 h-5 rounded border border-n-weak transition-transform hover:scale-110;
}
.tb-menu {
  @apply absolute z-50 top-full left-0 mt-1 rounded-lg border shadow-lg border-n-weak bg-n-solid-1;
}
.tb-menu-item {
  @apply px-2 py-1.5 text-sm text-left rounded-md text-n-slate-12 hover:bg-n-alpha-2;
}
.tb-primary {
  @apply px-3 py-1 text-sm font-medium rounded-md bg-n-brand text-white hover:opacity-90;
}
</style>
